import type { ProfileCookieSettings } from '@/api/core-api/types.gen';

import type { ResolvedConsent } from './consent-cookie';
import type { ConsentChoiceValue, ConsentChoices } from './consent-signals';

import { OPTIONAL_CONSENT_CATEGORIES } from './consent-signals';

/**
 * Mapping between the stored profile consent (`profile.cookieSettings`) and
 * the category choices the UI speaks.
 *
 * `performanceAnalyticsCookie` is the v1 field and stays the analytics
 * category — it is the only one with data behind it today, so it keeps
 * working unchanged. The rest are additive optional fields. A field that
 * was never written is `unset`, not `denied`: the visitor was never asked
 * about that category.
 */

function choiceFromOptionalBoolean(
  value: boolean | undefined,
): ConsentChoiceValue {
  if (value === true) return 'granted';
  if (value === false) return 'denied';
  return 'unset';
}

function optionalBooleanFromChoice(
  value: ConsentChoiceValue,
): boolean | undefined {
  if (value === 'unset') return undefined;
  return value === 'granted';
}

export function choicesFromProfileCookieSettings(
  settings: ProfileCookieSettings | undefined,
): ConsentChoices | null {
  if (!settings) return null;
  const {
    performanceAnalyticsCookie,
    functionalCookie,
    adMeasurementCookie,
    adPersonalizationCookie,
  } = settings;

  const hasAnyStoredChoice =
    performanceAnalyticsCookie !== undefined ||
    functionalCookie !== undefined ||
    adMeasurementCookie !== undefined ||
    adPersonalizationCookie !== undefined;
  if (!hasAnyStoredChoice) return null;

  return {
    functional: choiceFromOptionalBoolean(functionalCookie),
    analytics: choiceFromOptionalBoolean(performanceAnalyticsCookie),
    adMeasurement: choiceFromOptionalBoolean(adMeasurementCookie),
    adPersonalization: choiceFromOptionalBoolean(adPersonalizationCookie),
  };
}

/**
 * Version the profile's stored choice was made under. Settings written before
 * the category model carry no `consentVersion`, so any populated legacy row
 * reads as v1. `null` means the profile has never recorded a choice at all.
 */
export function profileConsentVersion(
  settings: ProfileCookieSettings | undefined,
): number | null {
  if (!settings) return null;
  if (typeof settings.consentVersion === 'number') {
    return settings.consentVersion;
  }
  return choicesFromProfileCookieSettings(settings) === null ? null : 1;
}

/**
 * A consent record complete enough to send over a wire that requires one.
 * `consentVersion` is present because it says which model the answers were
 * given under, and a record with answers but no version cannot be told apart
 * from a pre-category row. Every other field stays optional: a category the
 * visitor was never asked about is unanswered, not denied.
 */
export type SyncableProfileCookieSettings = ProfileCookieSettings & {
  consentVersion: number;
};

export function profileCookieSettingsFromChoices(
  choices: ConsentChoices,
  consentVersion: number,
): SyncableProfileCookieSettings {
  return {
    performanceAnalyticsCookie: optionalBooleanFromChoice(choices.analytics),
    functionalCookie: optionalBooleanFromChoice(choices.functional),
    adMeasurementCookie: optionalBooleanFromChoice(choices.adMeasurement),
    adPersonalizationCookie: optionalBooleanFromChoice(
      choices.adPersonalization,
    ),
    consentVersion,
  };
}

/**
 * The consent to seed an account with, or `null` when there is nothing to
 * persist. `ConsentProfileSync` holds the same `isSyncable` rule for its
 * write-through, which needs the record itself to diff against the profile.
 *
 * Two records look alike and are not: a decision, and this app's in-memory
 * reading of pre-unification cookies. `isSyncable` is the difference, and only
 * the first may be persisted — writing the second would turn a deny-wins merge
 * of cookies of unknown age into the account's recorded answer, at the version
 * it was guessed under, which then re-hydrates on every other device.
 *
 * What it persists is the record's own choices. An implied region default is
 * not in them and must not be: it is an overlay for one pageview, and an
 * account record outlives the policy that produced it.
 */
export function syncableProfileCookieSettings(
  stored: ResolvedConsent | null,
): SyncableProfileCookieSettings | null {
  if (!stored?.isSyncable) return null;
  return profileCookieSettingsFromChoices(stored.choices, stored.v);
}

export function choicesEqual(a: ConsentChoices, b: ConsentChoices): boolean {
  return OPTIONAL_CONSENT_CATEGORIES.every(
    (category) => a[category] === b[category],
  );
}
